import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/shared/lib/api'
import { AUTH } from '@/shared/lib/endpoints'
import { ROUTES } from '@/shared/lib/routes'
import { clearSilentAccess } from '@/shared/lib/credentials'
import { clearScopedStorage } from '@/shared/lib/storage'
import type { User, LoginPayload } from '@/shared/types/api'

export function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: () => api.get(AUTH.profile).json<User>(),
    retry: false,
  })
}

export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (credentials: LoginPayload) =>
      api.post(AUTH.login, { json: credentials }).json<User>(),
    // Awaiting the invalidation delays the caller's onSuccess (which navigates
    // to home) until stale queries are cleared — otherwise the home page can
    // render with cached data from the previous session and crash.
    onSuccess: async () => {
      await queryClient.invalidateQueries()
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.get(AUTH.logout).json(),
    onSuccess: async () => {
      await clearSilentAccess()
      queryClient.clear()
      // Drop every org-/user-scoped localStorage key so the next user logging
      // in on the same device never inherits the previous session's state
      // (e.g. the last-used bank account or the last-selected category).
      clearScopedStorage()
      if ('caches' in window) {
        const names = await caches.keys()
        await Promise.all(names.map((name) => caches.delete(name)))
      }
      window.location.href = ROUTES.login
    },
  })
}
